// This is tested in components/program/index.test.tsx

import SessionProgressBar from '@/components/session-progress-bar';
import { EnumButtonName } from '@calm/evexia-analytics';
import { MoreHoriz } from '@mui/icons-material';
import { Heading } from '@rhg/components/heading';
import { HeroHeaderWrapper } from '@rhg/components/ui/hero-header-wrapper';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import { useProgramActions } from '@rhg/hooks/use-program-actions';
import { useProgramItemCountText } from '@rhg/hooks/use-program-item-count-text';
import { useProvidedProgramContextWithGuidesAndWorkflow } from '@rhg/hooks/use-provided-program-context/with-guides-and-workflow';
import commonMessages from '@rhg/lang/messages/common';
import programMessages from '@rhg/lang/messages/program';
import {
    Box,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Theme,
    Typography,
} from '@rhg/libs/material';
import { CheckmarkCircleOutline, InfoOutline } from '@rhg/libs/material/icons';
import NextImage from '@rhg/libs/next/image';
import { ProgramState } from '@rhg/utils/programs';
import { MouseEvent, useId, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { HeaderPlaybackControls } from './header-playback-controls';

interface ProgramHeaderProps {
    closeMoreMenu: () => void;
    moreAnchor: HTMLElement | null;
    openMoreMenu: (e: MouseEvent<HTMLButtonElement>) => void;
    onLeaveProgramClicked: () => void;
    onJoinProgramClicked: () => void;
    onRestartProgramClicked: () => void;
    onShowHowToGetStartedDialogClicked: () => void;
}

const styleTitleContainer = {
    mb: 4,
};

const styleMoreButton = {
    borderRadius: '50%',
    color: 'action.active',
    ml: (theme: Theme) => theme.spacing(4),
};

const styleNumSessions = {
    ml: (theme: Theme) => theme.spacing(1),
};
const styleCategoryContainer = {
    display: 'flex',
    alignItems: 'center',
};
const styleNextContainer = {
    mt: (theme: Theme) => theme.ds.spacingUnits.CONTENT,
};
const styleCategoryIcon = {
    display: 'flex',
    alignItems: 'center',
    pr: 2,
};

const styleProgress = {
    mt: 8,
    mb: 9,
};

// Accent colour for the category label. Reads the effective theme (the header
// forces dark over the hero on desktop), so it tracks the rendered context
// rather than the app theme: brand accent (primary.main) on dark, primary.dark
// on light. The clinical "Calm Clinical" label keeps primary.dark in every theme.
const styleCategoryTitle = (isClinical: boolean) => (theme: Theme) => ({
    color:
        isClinical || theme.palette.mode !== 'dark'
            ? 'primary.dark'
            : 'primary.main',
});

export function ProgramHeader({
    closeMoreMenu,
    moreAnchor,
    openMoreMenu,
    onShowHowToGetStartedDialogClicked,
    onJoinProgramClicked,
    onLeaveProgramClicked,
    onRestartProgramClicked,
}: ProgramHeaderProps) {
    const { formatMessage } = useIntl();
    const {
        program,
        programType,
        programState,
        numSessions,
        numSessionsCompleted,
        packProgram,
        orderedSessions,
        packClass,
    } = useProvidedProgramContextWithGuidesAndWorkflow();
    const isClinical = programType === 'clinical_program';
    const itemCountText = useProgramItemCountText(program?.meditationType);

    const moreMenuId = useId();
    const moreButtonId = useId();

    const { handleNextButton } = useProgramActions();

    const categoryIcon = useMemo(() => {
        return isClinical ? (
            <NextImage
                src="/_n/images/ch-grad.png"
                alt=""
                width={24}
                height={24}
            />
        ) : undefined;
    }, [isClinical]);

    const isMobile = useIsMobile();
    const description = packProgram?.description ?? program?.description;

    const moreButton = useMemo(
        () => (
            <>
                <IconButton
                    size="legacyMedium"
                    trackButton="ProgramMore"
                    sx={styleMoreButton}
                    aria-controls={moreAnchor ? moreMenuId : undefined}
                    aria-haspopup="true"
                    aria-expanded={moreAnchor ? 'true' : undefined}
                    aria-label={formatMessage(
                        programMessages['program.program_actions']
                    )}
                    onClick={openMoreMenu}
                    id={moreButtonId}
                >
                    <MoreHoriz />
                </IconButton>
                <Menu
                    id={moreMenuId}
                    open={!!moreAnchor}
                    anchorEl={moreAnchor}
                    onClose={closeMoreMenu}
                    MenuListProps={{
                        'aria-labelledby': moreButtonId,
                    }}
                >
                    {programState === ProgramState.InProgress ? (
                        <MenuItem
                            trackButton="MenuLeaveProgram"
                            onClick={onLeaveProgramClicked}
                        >
                            <ListItemIcon>
                                <CheckmarkCircleOutline />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography variant="body1" color="text.body">
                                    {formatMessage(
                                        programMessages['program.leave_program']
                                    )}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    ) : programState !== ProgramState.Completed ? (
                        <MenuItem
                            trackButton="MenuJoinProgram"
                            onClick={onJoinProgramClicked}
                        >
                            <ListItemIcon>
                                <CheckmarkCircleOutline />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography variant="body1" color="text.body">
                                    {formatMessage(
                                        programMessages['program.join_program']
                                    )}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    ) : null}
                    <MenuItem
                        trackButton="MenuHowToGetStarted"
                        onClick={onShowHowToGetStartedDialogClicked}
                    >
                        <ListItemIcon>
                            <InfoOutline />
                        </ListItemIcon>
                        <ListItemText>
                            <Typography variant="body1" color="text.body">
                                {formatMessage(
                                    programMessages[
                                        'program.how_to_get_started'
                                    ]
                                )}
                            </Typography>
                        </ListItemText>
                    </MenuItem>
                    {programState === ProgramState.Completed && (
                        <MenuItem
                            onClick={onRestartProgramClicked}
                            trackButton="MenuRestartProgram"
                        >
                            <ListItemIcon>
                                <InfoOutline />
                            </ListItemIcon>
                            <ListItemText>
                                <Typography variant="body1" color="text.body">
                                    {formatMessage(
                                        programMessages[
                                            'program.restart_program'
                                        ]
                                    )}
                                </Typography>
                            </ListItemText>
                        </MenuItem>
                    )}
                </Menu>
            </>
        ),
        [
            closeMoreMenu,
            moreAnchor,
            moreButtonId,
            moreMenuId,
            onJoinProgramClicked,
            onLeaveProgramClicked,
            onShowHowToGetStartedDialogClicked,
            onRestartProgramClicked,
            openMoreMenu,
            programState,
            formatMessage,
        ]
    );

    const categoryTitle = useMemo(() => {
        return isClinical
            ? formatMessage(
                  commonMessages['common.program_type.clinical_program.title']
              )
            : program?.contentType;
    }, [isClinical, formatMessage, program?.contentType]);

    // Category metadata (icon + type + session count). Clinical programs always
    // show it; non-clinical programs show it only on larger viewports. The
    // session count is desktop-only (mobile clinical shows just the category).
    const showCategory = isClinical || !isMobile;
    const showSessionCount = !isMobile;

    return (
        <HeroHeaderWrapper
            forceDarkContent
            alignContentToImage
            heroImageSrc={
                packProgram?.image?.url ??
                program?.image?.url ??
                program?.backgroundImage?.url
            }
        >
            <Heading
                sx={styleTitleContainer}
                titleVariant={isMobile ? 'h5' : 'h4'}
                titleComponent="h1"
                titleColor="text.body"
                title={program?.title}
                subtitle={!isClinical ? description : undefined}
                rightButton={isMobile && isClinical && moreButton}
            />
            {showCategory && (
                <Box sx={styleCategoryContainer}>
                    {categoryIcon && (
                        <Box sx={styleCategoryIcon}>{categoryIcon}</Box>
                    )}
                    <Typography
                        variant="body2"
                        component="span"
                        sx={styleCategoryTitle(isClinical)}
                    >
                        {categoryTitle}
                    </Typography>
                    {showSessionCount && (
                        <Typography
                            variant="body2"
                            component="span"
                            sx={styleNumSessions}
                        >
                            {categoryTitle ? '• ' : null}
                            {itemCountText(numSessions)}
                        </Typography>
                    )}
                </Box>
            )}
            {!isMobile &&
                isClinical &&
                programState === ProgramState.InProgress &&
                numSessions && (
                    <SessionProgressBar
                        currentStep={numSessionsCompleted}
                        totalSteps={numSessions}
                        sx={styleProgress}
                        isClinicalProgram={isClinical}
                    />
                )}
            {!isMobile && (
                <Box sx={styleNextContainer}>
                    <HeaderPlaybackControls
                        guides={orderedSessions}
                        packClass={packClass}
                        program={program}
                        onNextButtonClick={handleNextButton()}
                        playTrackButton={EnumButtonName.ProgramHeaderNext}
                    />
                    {!isMobile && isClinical && moreButton}
                </Box>
            )}
        </HeroHeaderWrapper>
    );
}
